import { resolveIcon } from "@/features/stage/components/add-edit-stage/components/TheIconSelector";
import { queryClient } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Users,
  FileText,
  Plane,
  GraduationCap,
  GitBranch,
  UserCog,
  Megaphone,
  SlidersHorizontal,
  LayoutDashboard,
  UserPlus2,
  Cpu,
  BarChart3,
  Settings,
} from "lucide-react";

import axiosInstance from "@/lib/axios";
import { routerMainTabs } from "@/config/endpoints";
import useTenantFeatures from "./useTenantFeatures";
import { FEATURE_ROUTE_REGISTRY } from "@/routes/featureRouteRegistry";
// Icon mapping for resolveIcon
const iconMap: Record<string, React.ElementType> = {
  Users,
  FileText,
  Plane,
  GraduationCap,
  GitBranch,
  UserCog,
  Megaphone,
  SlidersHorizontal,
  LayoutDashboard,
  UserPlus2,
  Cpu,
  BarChart3,
  Settings,
};

const GROUP_DEFAULTS: Record<string, string> = {
  core: "STAGES",
  niche: "MODULE",
  management: "CONFIGURATIONS",
  // Mirrors the server's GROUP_LABEL_MAP (platform/tabs/tabs.services.ts).
  // The sidebar accordion keys its open-state by LABEL, so this must be unique.
  favorite: "FAVORITE VIEWS",
  // Connected tools with their own page (Razorpay); the server lists a tab
  // here only while that tool is connected.
  integration: "INTEGRATION",
};

function injectFeatureRoutes(
  groups: any[],
  hasFeature: (key: string) => boolean,
) {
  const result = groups.map((g: any) => ({ ...g, routes: [...g.routes] }));

  for (const [featureKey, routes] of Object.entries(FEATURE_ROUTE_REGISTRY)) {
    if (!hasFeature(featureKey)) continue;

    for (const route of routes) {
      const alreadyExists = result.some((g: any) =>
        g.routes.some((r: any) => r.key === route.key),
      );
      if (alreadyExists) continue;

      let group = result.find((g: any) => g.group === route.group);
      if (!group) {
        group = {
          group: route.group,
          label: GROUP_DEFAULTS[route.group] ?? route.group.toUpperCase(),
          routes: [],
        };
        result.push(group);
      }

      group.routes.push({
        key: route.key,
        path: route.path,
        component: route.component,
        label: route.label,
        icon: route.icon,
      });
    }
  }

  return result;
}

export default function useRouterData() {
  const { hasFeature } = useTenantFeatures();

  const {
    data: rawData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["router-tabs"],
    queryFn: async () => {
      const res = await axiosInstance.get(routerMainTabs);
      const tabs = res.data.tabs;

      const groups = tabs.groups.map((group: any) => ({
        ...group,
        routes: group.routes.map((route: any) => ({
          ...route,
          icon:
            resolveIcon({
              key: route?.key,
              iconName: route.icon,
            }) ||
            iconMap[route.icon] ||
            FileText,
        })),
      }));

      return {
        ...tabs,
        groups,

        dashboard: tabs.dashboard
          ? {
              ...tabs.dashboard,
              icon:
                resolveIcon({
                  key: tabs.dashboard.key,
                  iconName: tabs.dashboard.icon,
                }) ||
                iconMap[tabs.dashboard.icon] ||
                LayoutDashboard,
            }
          : undefined,
        analytics: tabs.report
          ? {
              ...tabs.report,
              icon:
                resolveIcon({
                  key: tabs.report.key,
                  iconName: tabs.report.icon,
                }) ||
                iconMap[tabs.report.icon] ||
                BarChart3,
            }
          : undefined,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  const data = useMemo(() => {
    if (!rawData) return rawData;
    return {
      ...rawData,
      groups: injectFeatureRoutes(rawData.groups, hasFeature),
    };
  }, [rawData, hasFeature]);

  const [delayedLoading, setDelayedLoading] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setDelayedLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
    setDelayedLoading(true);
  }, [isLoading]);

  const {
    analyticModules,
    stageModules,
    nicheModules,
    managementModules,
    integrationModules,
    favoriteViewModules,
  } = useMemo(() => {
      if (!data) {
        return {
          analyticModules: [],
          stageModules: [],
          nicheModules: [],
          managementModules: [],
          integrationModules: [],
          favoriteViewModules: [],
        };
      }

      const { groups = [], ...outsideModules } = data;

      const groupsMap = Object.fromEntries(
        groups.map((g: any) => [
          g.group,
          g.routes.map((route: any) => ({
            ...route,
          })),
        ])
      );

      return {
        // ✅ Analytics tabs ALSO get icons now
        analyticModules: Object.values(outsideModules)
          .filter(Boolean)
          .map((item: any) => ({
            ...item,
            icon: resolveIcon({
              key: item?.key,
              iconName: item?.icon,
            }),
          })),

        stageModules: groupsMap.core ?? [],
        nicheModules: groupsMap.niche ?? [],
        managementModules: groupsMap.management ?? [],
        // INTEGRATION tabs (Razorpay) — present only while connected, so the
        // role editor can grant the module exactly when it exists.
        integrationModules: groupsMap.integration ?? [],
        // SHARED views only — a personal view needs no role grant, and listing
        // one in the role editor would invite an admin to "grant" a key that
        // does nothing.
        favoriteViewModules: (groupsMap.favorite ?? []).filter(
          (route: any) => route?.isShared,
        ),
      };
    }, [data]);

  return {
    tabs: data,
    isLoading: delayedLoading,
    // Raw in-flight flag. `isLoading` is false during a REFETCH (data already
    // exists), so route fallbacks must read this to avoid judging a URL
    // against the outgoing session's route table.
    isFetching,
    analyticModules,
    stageModules,
    nicheModules,
    managementModules,
    integrationModules,
    favoriteViewModules,
    error,
    refetch,
  };
}

export const refetchRoutes = () => {
  return queryClient.invalidateQueries({ queryKey: ["router-tabs"] });
};

/**
 * Hard-drop the route table when the SESSION changes (login / token swap).
 *
 * `refetchRoutes` (invalidate) keeps serving the previous tenant's tabs until
 * the refetch lands — and those paths embed the old tenant id, so the router
 * would briefly mount routes that can never resolve for the new user. Removing
 * the queries makes `tabs` undefined instead, which the route fallbacks render
 * as a loader. `tenant-features` goes too: it feeds `injectFeatureRoutes`.
 */
export const resetRoutesForNewSession = () => {
  queryClient.removeQueries({ queryKey: ["router-tabs"] });
  queryClient.removeQueries({ queryKey: ["tenant-features"] });
};
